import { useState, useEffect } from 'react';
import { getShareView, stripeCheckout } from '../api';

const fmt = (n) => `$${Math.abs(n).toFixed(2)}`;

export default function ShareView({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getShareView(token)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--light)' }}>
      <p style={{ color: 'var(--muted)' }}>Loading…</p>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--light)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🏒</div>
        <h2 style={{ color: 'var(--navy)' }}>Page Not Found</h2>
        <p style={{ color: 'var(--muted)', marginTop: 8 }}>This link may be invalid or expired.</p>
      </div>
    </div>
  );

  const balance = data.currentBalance;
  const transactions = data.transactions || [];
  const schedules = data.paymentSchedules || [];
  const venmo = data.settings?.venmoUsername;
  const announcement = data.announcement;
  const stripeEnabled = data.stripeEnabled;

  const totalPaid = transactions.filter(t => t.type === 'payment').reduce((s, t) => s + t.amount, 0);
  const totalCredits = transactions.filter(t => t.type === 'credit').reduce((s, t) => s + t.amount, 0);

  // Collect all upcoming payments across all schedules, sorted by due date
  const allUpcoming = [];
  for (const sched of schedules) {
    const schedPaid = transactions.filter(t => t.scheduleId === sched.scheduleId)
          .reduce((s, t) => {
            if (t.type === 'payment' || t.type === 'credit') return s + t.amount;
            if (t.type === 'adjustment') return s + t.amount;
            return s;
          }, 0);
    let cumulative = 0;
    for (const item of sched.schedule) {
      cumulative += item.amount;
      const isPaid = schedPaid >= cumulative;
      const overdue = !isPaid && new Date(item.dueDate) < new Date();
      allUpcoming.push({ ...item, scheduleTitle: sched.title, isPaid, overdue });
    }
  }
  allUpcoming.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  const nextDue = allUpcoming.find(p => !p.isPaid);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light)' }}>
      {/* Header */}
      <div style={{ background: 'var(--navy)', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 28 }}>🏒</span>
        <div>
          <div style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>Orchard Lake Red 2016</div>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>Budget Tracker</div>
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 16px' }}>

        {/* Balance Hero */}
        <div className="card" style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 4 }}>Balance for</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--navy)', marginBottom: 16 }}>{data.playerName}</div>
          <div style={{ fontSize: 56, fontWeight: 900, color: balance > 0 ? 'var(--red)' : 'var(--green)' }}>
            {balance > 0 ? `$${balance.toFixed(2)}` : `✓ $0.00`}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 8 }}>
            {balance > 0 ? 'Balance due' : 'Paid in full 🎉'}
          </div>

          {/* Payment buttons */}
          {balance > 0 && (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 16 }}>
              {venmo && (
                <a href={`venmo://paycharge?txn=pay&recipients=${venmo}&amount=${balance.toFixed(2)}&note=DuesTrack Payment`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: '#008CFF', color: 'white', padding: '10px 24px', borderRadius: 8,
                    textDecoration: 'none', fontWeight: 700, fontSize: 15 }}>
                  <span style={{ fontSize: 20 }}>💸</span> Pay with Venmo
                </a>
              )}
              {stripeEnabled && <PayWithCardButton data={data} balance={balance} />}
            </div>
          )}
          {data.startingBalance > 0 && (
            <div style={{ margin: '16px 0 4px' }}>
              <div style={{ background: '#e5e7eb', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                <div style={{
                  background: balance <= 0 ? 'var(--green)' : 'var(--navy)',
                  width: `${Math.min(100, ((data.startingBalance - balance) / data.startingBalance) * 100)}%`,
                  height: '100%', borderRadius: 6, transition: 'width 0.4s'
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginTop: 5 }}>
                <span>${(totalPaid + totalCredits).toFixed(2)} paid</span>
                <span>Total: ${data.startingBalance.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Announcement banner */}
        {announcement?.text && (
          <div style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b', borderRadius: 10, padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 20 }}>📢</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>Announcement</div>
              <div style={{ fontSize: 14, color: '#1a1a2e' }}>{announcement.text}</div>
            </div>
          </div>
        )}

        {/* Next Payment Due — prominent callout */}
        {nextDue && (
          <div style={{
            background: nextDue.overdue ? '#fee2e2' : '#eff6ff',
            border: `2px solid ${nextDue.overdue ? 'var(--red)' : 'var(--navy)'}`,
            borderRadius: 10, padding: '16px 20px', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: nextDue.overdue ? 'var(--red)' : 'var(--navy)', marginBottom: 4 }}>
                {nextDue.overdue ? '⚠ Payment Overdue' : '📅 Next Payment Due'}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>${nextDue.amount.toFixed(2)}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
                {nextDue.scheduleTitle} — {nextDue.description} · Due {nextDue.dueDate}
              </div>
            </div>
          </div>
        )}

        {/* All Schedules */}
        {schedules.length > 0 && <SchedulesList schedules={schedules} transactions={transactions} />}

        {/* Transaction History */}
        {transactions.length > 0 && (
          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Transaction History</div>
            <div className="txn-list">
              <div className="txn-row header">
                <span>Date</span><span>Description</span><span>Type</span><span>Amount</span><span></span>
              </div>
              {[...transactions].sort((a, b) => new Date(a.date) - new Date(b.date) || new Date(a.createdAt) - new Date(b.createdAt)).map(t => {
                // From parent's perspective: payments/credits reduce balance (good = green), expenses increase it (charge = red)
                const reducesBalance = t.type === 'payment' || t.type === 'credit';
                const friendlyType = { payment: 'Payment', credit: 'Credit', expense: 'Charge', adjustment: 'Adjustment' }[t.type] || t.type;
                return (
                  <div key={t.transactionId} className="txn-row">
                    <span style={{ color: 'var(--muted)' }}>{t.date}</span>
                    <span>{t.description || friendlyType}</span>
                    <span><span className={`badge badge-${t.type}`}>{friendlyType}</span></span>
                    <span className={reducesBalance ? 'txn-amount-positive' : 'txn-amount-negative'}>
                      {reducesBalance ? '-' : '+'}${Math.abs(t.amount).toFixed(2)}
                    </span>
                    <span></span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12, marginTop: 24 }}>
          Orchard Lake Red 2016 Hockey · Questions? Contact your team manager.
        </div>
      </div>
    </div>
  );
}

// ─── Pay with Card Button ─────────────────────────────────────────────────────

function PayWithCardButton({ data, balance }) {
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState(balance.toFixed(2));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const base = data.apiBase || '';

  // Calculate surcharge preview whenever amount changes
  useEffect(() => {
    const val = parseFloat(amount);
    if (!val || val < 0.5) { setPreview(null); return; }
    const STRIPE_RATE = 0.029;
    const APP_RATE = 0.005;
    const desiredCents = Math.round(val * 100);
    const grossCents = Math.round((desiredCents + 30) / (1 - STRIPE_RATE - APP_RATE));
    const surchargeCents = grossCents - desiredCents;
    setPreview({ surcharge: (surchargeCents / 100).toFixed(2), gross: (grossCents / 100).toFixed(2) });
  }, [amount]);

  const handlePay = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await stripeCheckout({
        playerId: data.playerId,
        shareToken: data.shareToken,
        amount: parseFloat(amount),
      });
      if (res.url) window.location.href = res.url;
      else setError(res.error || 'Payment setup failed');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const params = new URLSearchParams(window.location.search);
  if (params.get('payment') === 'success') {
    return (
      <div style={{ background: '#d1fae5', border: '1px solid #10b981', borderRadius: 8, padding: '10px 20px', color: '#065f46', fontWeight: 700 }}>
        ✓ Payment received! Your balance will update shortly.
      </div>
    );
  }

  return (
    <>
      <button onClick={() => setShowModal(true)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#1a1a2e', color: 'white', padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
        💳 Pay with Card
      </button>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowModal(false)}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: '#1a1a2e' }}>💳 Pay with Card</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              Secure payment via Stripe. A processing fee is added when paying by card.
            </p>
            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}
            <form onSubmit={handlePay}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 5 }}>
                  Payment Amount ($)
                </label>
                <input type="number" step="0.01" min="0.50" max={balance.toFixed(2)}
                  value={amount} onChange={e => setAmount(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #dde3ed', borderRadius: 7, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }} />
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Balance owed: ${balance.toFixed(2)} · Partial payments accepted
                </div>
              </div>

              {/* Surcharge breakdown */}
              {preview && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#374151', marginBottom: 6 }}>
                    <span>Payment amount</span><span>${parseFloat(amount).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                    <span>Processing fee (3.4% + $0.30)</span><span>+${preview.surcharge}</span>
                  </div>
                  <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 16, color: '#1a1a2e' }}>
                    <span>Total charged to card</span><span>${preview.gross}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                    Your balance will be reduced by ${parseFloat(amount).toFixed(2)}
                  </div>
                </div>
              )}

              <button type="submit" disabled={loading || !preview}
                style={{ width: '100%', background: '#1a3c6e', color: 'white', padding: '13px', borderRadius: 8, border: 'none', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 16, opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Redirecting to Stripe…' : preview ? `Pay $${preview.gross} total` : 'Enter amount'}
              </button>
            </form>
            <button onClick={() => setShowModal(false)}
              style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14, padding: '8px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Schedules List (collapsible per schedule) ────────────────────────────────

function SchedulesList({ schedules, transactions }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>📋 Payment Schedules</div>
      {schedules.map(sched => {
        const schedPaid = transactions.filter(t => t.scheduleId === sched.scheduleId)
          .reduce((s, t) => {
            if (t.type === 'payment' || t.type === 'credit') return s + t.amount;
            if (t.type === 'adjustment') return s + t.amount;
            return s;
          }, 0);
        const isFullyPaid = schedPaid >= sched.totalAmount;
        const open = expanded[sched.scheduleId];

        return (
          <div key={sched.scheduleId} style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => toggle(sched.scheduleId)}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{sched.title}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                  ${schedPaid.toFixed(2)} of ${sched.totalAmount.toFixed(2)} paid
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className={`badge ${isFullyPaid ? 'badge-payment' : 'badge-credit'}`}>
                  {isFullyPaid ? '✓ Paid' : 'In Progress'}
                </span>
                <span style={{ color: 'var(--muted)', fontSize: 18 }}>{open ? '▲' : '▼'}</span>
              </div>
            </div>

            {open && (
              <div className="plan-grid" style={{ marginTop: 12 }}>
                {sched.schedule.map((s, i) => {
                  const cumulative = sched.schedule.slice(0, i + 1).reduce((sum, x) => sum + x.amount, 0);
                  const isPaid = schedPaid >= cumulative;
                  const overdue = !isPaid && new Date(s.dueDate) < new Date();
                  return (
                    <div key={i} className={`plan-month ${isPaid ? 'paid' : overdue ? 'overdue' : 'due'}`}>
                      <div className="month-label" style={{ fontSize: 12 }}>{s.description}</div>
                      <div className="month-date">Due: {s.dueDate}</div>
                      <div className="month-amount">${s.amount.toFixed(2)}</div>
                      <div style={{ fontSize: 11, marginTop: 4 }}>{isPaid ? '✓ Paid' : overdue ? '⚠ Overdue' : 'Upcoming'}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
